<script setup lang="ts">
    import { useI18n } from "vue-i18n";
    import Button from "@/public/vue/Common/Components/Buttons/Button.vue";
    import { ButtonType } from "@/public/vue/Common/Composables/button-styles";
    import ActionLink from "@/public/vue/Common/Components/Buttons/ActionLink.vue";
    import { computed, onBeforeMount, onMounted, ref } from "vue";
    import { useConsent } from "../consent";

    const isInitialized = ref(false);
    const hasInteracted = ref(true);
    const { t } = useI18n();
    useConsent();

    const attemptInitFromKlaro = () => {
        if ((window as any).klaro && isInitialized.value === false) {
            const manager = (window as any).klaro.getManager();
            hasInteracted.value = manager.confirmed;
            manager.watch({
                update: (manager: any, eventType: any) => {
                    if (eventType === 'saveConsents') {
                        hasInteracted.value = true;
                    }
                }
            });
            isInitialized.value = true;
        }
    };

    onBeforeMount(() => {
        window.addEventListener('klaroLoaded', attemptInitFromKlaro);
    });

    onMounted(() => attemptInitFromKlaro());

    const refuseAll = () => {
        const manager = (window as any).klaro.getManager();
        manager.changeAll(false);
        manager.saveAndApplyConsents();
        hasInteracted.value = true;
    };

    const acceptAll = () => {
        const manager = (window as any).klaro.getManager();
        manager.changeAll(true);
        manager.saveAndApplyConsents();
        hasInteracted.value = true;
    };

    const customize = () => {
        (window as any).klaro.show(undefined, true);
    };

    const allowCompleteRefusal = computed(() => {
        const gdprCountries = [
            "AT", "BE", "BG", "CY",  "CH", "CZ", "DE", "DK", "EE", "ES", "FI", "FR",
            "GB", "GR", "HR", "HU", "IE","IS", "IT", "LI", "LT", "LU", "LV", "MT", "NL", "NO",
            "PL", "PT", "RO", "SE", "SI", "SK"
        ];

        const country = navigator.language.includes('-') ? navigator.language.split('-')[1] : navigator.language;
        return gdprCountries.includes(country.toUpperCase());
    });
</script>
<template>
    <div class="cookie-consent-modal" v-if="!hasInteracted">
        <div v-if="allowCompleteRefusal">
            <ActionLink @click="refuseAll">{{ t('consent.modal.refuse_all') }}</ActionLink>
        </div>
        <p class="consent-title">{{ t('consent.modal.title') }}</p>
        <p class="consent-description">{{ t('consent.modal.description') }}</p>
        <div class="actions-container">
            <Button :type="ButtonType.LINK" @click="customize">{{ t('consent.modal.customize') }}</Button>
            <Button :type="ButtonType.PRIMARY" @click="acceptAll">{{ t('consent.modal.accept_all') }}</Button>
        </div>
    </div>
</template>
<style lang="scss">
@import '@Common/Styles/variables';

.cookie-notice {
    display: none !important;
}

.cookie-consent-modal {
    position: fixed;
    width: var(--yapla-consent-modal-width, 405px);
    z-index: var(--yapla-consent-modal-z-index, 999999);
    bottom: var(--yapla-consent-modal-bottom, 25px);
    right: var(--yapla-consent-modal-right, auto);
    left: var(--yapla-consent-modal-left, 25px);
    top: var(--yapla-consent-modal-top, auto);
    background: var(--yapla-consent-modal-background, #ffffff);
    display: flex;
    flex-direction: column;
    padding: var(--yapla-consent-modal-padding, 20px);
    border-radius: var(--yapla-consent-modal-radius, 6px);
    gap: var(--yapla-consent-modal-gap, 10px);
    box-shadow: var(--yapla-consent-modal-shadow, 0px 2px 3px rgba(0, 0, 0, 0.2), 0px 0px 15px rgba(0, 0, 0, 0.1));

    .consent-title {
        font-size: var(--yapla-consent-title-size, 20px);
        font-weight: var(--yapla-consent-title-weight, 600);
        color: var(--yapla-consent-title-color, var(--yapla-gray-700, #{map-get($yapla-gray, 700)}));
    }

    .consent-description {
        font-size: var(--yapla-consent-description-size, 15px);
        font-weight: var(--yapla-consent-description-weight, 500);
        color: var(--yapla-consent-description-color, var(--yapla-gray-600, #{map-get($yapla-gray, 600)}));
    }

    .actions-container {
        display: flex;
        justify-content: flex-end;
        gap: var(--yapla-consent-actions-gap, 10px);
        flex-wrap: wrap;
        padding-top: 10px;

        .btn {
            margin: 0;
        }
    }

    @media screen and (max-width: 450px) {
        width: var(--yapla-consent-modal-width, auto);
        top: var(--yapla-consent-modal-top, auto);
        right: var(--yapla-consent-modal-right, 5px);
        left: var(--yapla-consent-modal-left, 5px);
        bottom: var(--yapla-consent-modal-bottom, 5px);
        padding: var(--yapla-consent-modal-padding, 15px);
    }
}

</style>
